import React, { useMemo, useState } from 'react';
import {
  OrgSubscription,
  PricingTierVersion,
  SystemAdminDataService,
  formatPrice,
} from '../../services/systemAdminDataService';

interface SubscriptionEditModalProps {
  orgId: number;
  orgName: string;
  current: OrgSubscription;
  /** Current versions of every tier, for the picker. */
  tiers: PricingTierVersion[];
  onClose: () => void;
  /** Called after a successful save so the panel can refetch. */
  onSaved: () => void;
}

const STATUS_OPTIONS: { value: OrgSubscription['status']; label: string; hint: string }[] = [
  { value: 'trial',      label: 'Trial',      hint: 'Evaluating, not yet paying' },
  { value: 'active',     label: 'Active',     hint: 'Paying and in service' },
  { value: 'past_due',   label: 'Past due',   hint: 'Payment missed, still in service' },
  { value: 'suspended',  label: 'Suspended',  hint: 'Access blocked. Nothing is deleted' },
  { value: 'cancelled',  label: 'Cancelled',  hint: 'Ending at the close of the term' },
  { value: 'terminated', label: 'Terminated', hint: 'Agreement over' },
];

const today = () => new Date().toISOString().slice(0, 10);

/**
 * Change an organisation's tier, status or billing period.
 *
 * This is a change of CONTRACT, not an edit: it closes the open period and
 * opens a new one, so both survive in the history and "what were they paying in
 * March" keeps answering March. Overrides and discounts carry forward — the
 * panel's own controls change those.
 */
const SubscriptionEditModal: React.FC<SubscriptionEditModalProps> = ({
  orgId, orgName, current, tiers, onClose, onSaved,
}) => {
  const [tierCode, setTierCode] = useState(current.tierCode);
  const [status, setStatus] = useState<OrgSubscription['status']>(current.status);
  const [billingPeriod, setBillingPeriod] = useState<OrgSubscription['billingPeriod']>(current.billingPeriod);
  const [effectiveDate, setEffectiveDate] = useState(today());
  const [notes, setNotes] = useState(current.notes ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Only tiers still on sale, plus whatever they are on now — an org must never
  // be blocked from saving because its own tier was retired.
  const sellable = useMemo(
    () => tiers.filter(t => t.isCurrent && (t.isSellable || t.code === current.tierCode)),
    [tiers, current.tierCode]
  );

  const chosen = sellable.find(t => t.code === tierCode);
  const newPrice = chosen
    ? (billingPeriod === 'annual' ? chosen.annualPrice : chosen.monthlyPrice)
    : null;
  const changed =
    tierCode !== current.tierCode ||
    status !== current.status ||
    billingPeriod !== current.billingPeriod;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await SystemAdminDataService.changeSubscription(orgId, {
        tierCode, status, billingPeriod, effectiveDate,
        notes: notes.trim() || null,
      });
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'The change could not be saved.');
      setSaving(false);
    }
  };

  return (
    <div className="modal show d-block" tabIndex={-1} role="dialog" style={{ background: 'rgba(13,77,26,0.35)' }}>
      <div className="modal-dialog modal-dialog-centered" role="document">
        <div className="modal-content ivory-panel">
          <div className="modal-header">
            <h5 className="modal-title">Change subscription</h5>
            <button type="button" className="btn-close" onClick={onClose} aria-label="Close" disabled={saving} />
          </div>

          <div className="modal-body">
            <p className="sys-field__note mb-3">
              Closes {orgName}&rsquo;s current period and opens a new one. The
              existing period stays in the history.
            </p>

            <div className="mb-3">
              <label className="form-label" htmlFor="sub-tier">Tier</label>
              <select
                id="sub-tier"
                className="form-select"
                value={tierCode}
                onChange={e => setTierCode(e.target.value)}
                disabled={saving}
              >
                {sellable.map(t => (
                  <option key={t.code} value={t.code}>
                    {t.displayName} — {t.maxChildren == null ? 'unlimited' : `up to ${t.maxChildren}`} children
                    {t.monthlyPrice != null ? ` · ${formatPrice(t.monthlyPrice, t.currency)}/mo` : ' · POA'}
                  </option>
                ))}
              </select>
              {chosen && !chosen.aiInsightsIncluded && current.aiFullEnabled && current.aiFullIsOverride && (
                <div className="form-text">
                  This tier does not include full AI, but this organisation has an
                  override forcing it on — so it keeps it.
                </div>
              )}
            </div>

            <div className="mb-3">
              <label className="form-label" htmlFor="sub-status">Status</label>
              <select
                id="sub-status"
                className="form-select"
                value={status}
                onChange={e => setStatus(e.target.value as OrgSubscription['status'])}
                disabled={saving}
              >
                {STATUS_OPTIONS.map(s => (
                  <option key={s.value} value={s.value}>{s.label} — {s.hint}</option>
                ))}
              </select>
              {(status === 'terminated' || status === 'cancelled') && (
                <div className="form-text">
                  This records the commercial state only. It sets no deletion
                  clock and removes no data.
                </div>
              )}
            </div>

            <div className="row g-3 mb-3">
              <div className="col-sm-6">
                <label className="form-label" htmlFor="sub-period">Billing</label>
                <select
                  id="sub-period"
                  className="form-select"
                  value={billingPeriod}
                  onChange={e => setBillingPeriod(e.target.value as OrgSubscription['billingPeriod'])}
                  disabled={saving}
                >
                  <option value="monthly">Monthly</option>
                  <option value="annual">Annual — two months free</option>
                </select>
              </div>
              <div className="col-sm-6">
                <label className="form-label" htmlFor="sub-date">Effective from</label>
                <input
                  id="sub-date"
                  type="date"
                  className="form-control"
                  value={effectiveDate}
                  onChange={e => setEffectiveDate(e.target.value)}
                  disabled={saving}
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label" htmlFor="sub-notes">Notes</label>
              <textarea
                id="sub-notes"
                className="form-control"
                rows={2}
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Why this changed — useful a year from now"
                disabled={saving}
              />
            </div>

            {/* State the outcome as a fact before they commit to it. */}
            {changed && (
              <div className="sys-notice">
                <i className="fas fa-arrow-right-arrow-left" aria-hidden="true" />
                <span>
                  From <strong>{new Date(`${effectiveDate}T00:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}</strong>,
                  {' '}{orgName} moves to <strong>{chosen?.displayName ?? tierCode}</strong>
                  {newPrice != null && <> at <strong>{formatPrice(newPrice, chosen?.currency ?? 'GBP')}</strong>/{billingPeriod === 'annual' ? 'yr' : 'mo'}</>}
                  {chosen?.maxChildren != null && <>, permitting {chosen.maxChildren} children</>}.
                </span>
              </div>
            )}

            {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="button" className="btn btn-success" onClick={save} disabled={saving || !changed}>
              {saving ? 'Saving…' : 'Change subscription'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionEditModal;
